import React, { createContext, useContext, useReducer, useEffect } from "react";
import NativeWebSocketService from "./nativeWebSocketService";

const NotificationContext = createContext();

const notificationReducer = (state, action) => {
  switch (action.type) {
    case "ADD_NOTIFICATION":
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 50),
      };
    case "REMOVE_NOTIFICATION":
      return {
        ...state,
        notifications: state.notifications.filter(
          (n) => n.id !== action.payload
        ),
      };
    case "CLEAR_NOTIFICATIONS":
      return {
        ...state,
        notifications: [],
      };
    case "SET_CONNECTION_STATUS":
      return {
        ...state,
        isConnected: action.payload,
      };
    default:
      return state;
  }
};

export const NotificationProvider = ({ children, authToken }) => {
  const [state, dispatch] = useReducer(notificationReducer, {
    notifications: [],
    isConnected: false,
  });

  useEffect(() => {
    if (!authToken) {
      console.warn(" No auth token for WebSocket");
      return;
    }

    // Request browser notification permission
    NativeWebSocketService.requestNotificationPermission();

    // Connect to WebSocket
    NativeWebSocketService.connect(
      authToken,
      () => {
        dispatch({ type: "SET_CONNECTION_STATUS", payload: true });
        // console.log(" WebSocket connected successfully");
      },
      (error) => {
        dispatch({ type: "SET_CONNECTION_STATUS", payload: false });
        console.error(" WebSocket connection failed:", error);
      }
    );

    // Listen for custom events from WebSocket service
    const handleNewOrder = (event) => {
      const notification = {
        id: Date.now() + Math.random(),
        type: "new_order",
        title: "🆕 New Order",
        message: `Order #${event.detail.orderId} from ${event.detail.customerName}`,
        amount: event.detail.totalAmount,
        orderId: event.detail.orderId,
        timestamp: new Date().toLocaleTimeString(),
        data: event.detail,
      };
      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
    };

    const handleOrderUpdate = (event) => {
      const notification = {
        id: Date.now() + Math.random(),
        type: "order_update",
        title: "📝 Order Updated",
        message: `Order #${event.detail.orderId} status: ${event.detail.status}`,
        orderId: event.detail.orderId,
        timestamp: new Date().toLocaleTimeString(),
        data: event.detail,
      };
      dispatch({ type: "ADD_NOTIFICATION", payload: notification });
    };

    window.addEventListener("newOrderNotification", handleNewOrder);
    window.addEventListener("orderUpdateNotification", handleOrderUpdate);

    // Cleanup on unmount
    return () => {
      NativeWebSocketService.disconnect();
      window.removeEventListener("newOrderNotification", handleNewOrder);
      window.removeEventListener("orderUpdateNotification", handleOrderUpdate);
    };
  }, [authToken]);

  const addNotification = (notification) => {
    dispatch({ type: "ADD_NOTIFICATION", payload: notification });
  };

  const removeNotification = (id) => {
    dispatch({ type: "REMOVE_NOTIFICATION", payload: id });
  };

  const clearNotifications = () => {
    dispatch({ type: "CLEAR_NOTIFICATIONS" });
  };

  const value = {
    notifications: state.notifications,
    isConnected: state.isConnected,
    addNotification,
    removeNotification,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider"
    );
  }
  return context;
};
